import React from 'react';
import { Select, Tag, Table, Button, Popover, Avatar, Tree, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import type { TreeDataNode } from 'antd';
import { ReactComponent as CampaignSvg } from '@assets/icons/compaings.svg';
import { ReactComponent as PrivateEyeSvg } from '@assets/icons/eye-private.svg';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as FolderPrivateSvg } from '@assets/icons/folder-private.svg';
import { ReactComponent as ArrowRight } from '@assets/icons/arrow-right_black_without-line.svg';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { AntTreeNodeProps } from 'antd/lib/tree';
import UserAvatar from '@components/UserAvatar';
import { useOrganization } from '@components/OrganizationBoundary';
import { hashedColor } from '@helpers/hashedColor';
import { toggleWorkspaceMemberGroupModal } from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { apiClient } from '@api/client';
import {
  OrganizationRoleCopy,
  WorkspaceRole,
  WorkspaceRoleCopy
} from '@helpers/userRoles';
import {
  ResendWorkspaceInvitationMemberDto,
  WorkspaceMemberDto
} from '@api/Api';
import { useFolderTreeQueries } from '@hooks/queries/folders';

const activeStyle = {
  background: '#92D1BA',
  color: '#161819',
  border: 'none',
  padding: '2px 9px'
};

const pendingStyle = {
  background: '#FAECCC',
  color: '#161819',
  border: 'none',
  padding: '2px 9px'
};

const expiredStyle = {
  background: '#F5C8BE',
  color: '#161819',
  border: 'none',
  padding: '2px 9px'
};

interface CampaignTreeNode extends TreeDataNode {
  id: string;
}

interface FolderTreeNode extends TreeDataNode {
  id: string;
  campaignId: string;
  isPrivate: boolean;
}

function TreeSwitcherIcon(props: AntTreeNodeProps) {
  return <ArrowRight style={{ rotate: props.expanded ? '90deg' : '0deg' }} />;
}

function MemberProjectsPopover({
  memberId,
  campaignsCount,
  workspaceId
}: {
  memberId: string;
  campaignsCount: number;
  workspaceId: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<
    { campaignId: string; folderId?: string }[]
  >([]);

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['workspace', 'member-campaigns', workspaceId, memberId],
    queryFn: async () => {
      const { data } =
        await apiClient.workspace.workspaceControllerListMemberCampaigns({
          workspaceId,
          memberId
        });
      return data.campaigns;
    },
    enabled: open && campaignsCount > 0
  });

  const folderQueries = useFolderTreeQueries(
    { workspaceId, expanded },
    { enabled: open }
  );

  const treeData = campaignsData?.map((campaign) => {
    function getChildren(
      campaignId: string,
      folderId?: string
    ): { isLoading: boolean; data: FolderTreeNode[] } | undefined {
      const queryIndex = expanded.findIndex(
        (item) => item.campaignId === campaignId && item.folderId === folderId
      );
      if (queryIndex === -1) {
        return undefined;
      }
      const query = folderQueries[queryIndex];
      return {
        isLoading: query.isLoading,
        data:
          query.data?.map((folder) => {
            const children = getChildren(campaignId, folder.id);
            return {
              id: folder.id,
              key: `folder-${folder.id}`,
              isLeaf: folder.foldersCount === 0,
              campaignId,
              isPrivate: folder.isPrivate,
              title: (
                <Link
                  className="user_campaigns__folder"
                  to={`/campaigns/single/${campaignId}/${folder.id}`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="user_campaigns__folder-icon">
                    {folder.isPrivate ? <FolderPrivateSvg /> : <FolderSvg />}
                  </span>
                  <span className="user_campaigns__folder-name">
                    {folder.name}
                  </span>
                </Link>
              ),
              switcherIcon: children?.isLoading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
                />
              ) : undefined,
              children: children?.data
            } satisfies FolderTreeNode;
          }) ?? []
      };
    }
    const children = getChildren(campaign.id);
    return {
      id: campaign.id,
      key: `campaign-${campaign.id}`,
      isLeaf: campaign.foldersCount === 0,
      title: (
        <Link
          className="user_campaigns__campaign"
          to={`/campaigns/single/${campaign.id}`}
          target="_blank"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="user_campaigns__campaign-icon">
            <CampaignSvg />
          </span>
          <span className="user_campaigns__campaign-name">{campaign.name}</span>
          {campaign.isPrivate && (
            <span className="user_campaigns__campaign-icon-private">
              <PrivateEyeSvg />
            </span>
          )}
        </Link>
      ),
      switcherIcon: children?.isLoading ? (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
      ) : undefined,
      children: children?.data
    } satisfies CampaignTreeNode;
  });

  return (
    <Popover
      content={
        <div
          style={{
            minWidth: 200,
            maxHeight: 300,
            display: isLoading ? 'none' : undefined
          }}
        >
          <Tree
            showIcon
            showLine
            treeData={treeData}
            switcherIcon={TreeSwitcherIcon}
            expandedKeys={expanded.map((x) =>
              x.folderId ? `folder-${x.folderId}` : `campaign-${x.campaignId}`
            )}
            onExpand={(_, { expanded, node }) => {
              if (node.key.startsWith('campaign-')) {
                const typedNode = node as CampaignTreeNode;
                const campaignId = typedNode.id;
                setExpanded((prev) => {
                  if (expanded) {
                    return [...prev, { campaignId }];
                  }
                  return prev.filter((item) => item.campaignId !== campaignId);
                });
              }
              if (node.key.startsWith('folder-')) {
                const typedNode = node as unknown as FolderTreeNode;
                const { campaignId } = typedNode;
                const folderId = typedNode.id;
                setExpanded((prev) => {
                  if (expanded) {
                    return [...prev, { campaignId, folderId }];
                  }
                  return prev.filter(
                    (item) =>
                      item.campaignId !== campaignId ||
                      item.folderId !== folderId
                  );
                });
              }
            }}
            selectable={false}
          />
        </div>
      }
      className={classNames({
        user_campaigns: true,
        'user_campaigns--empty': !campaignsCount
      })}
      placement="bottomLeft"
      overlayClassName="user_campaigns__popover"
      trigger={campaignsCount ? ['hover'] : []}
      open={open}
      onOpenChange={setOpen}
    >
      {open && isLoading ? (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
      ) : (
        <span>{campaignsCount > 100 ? '100+' : campaignsCount}</span>
      )}
    </Popover>
  );
}

interface IWorkspaceMembersTable {
  edges: WorkspaceMemberDto[] | undefined | null;
  count: number;
  selectedIds: string[];
  setMembersToManage?: (
    selectedIds: string[],
    members: WorkspaceMemberDto[],
    isAllSelect: boolean
  ) => void;
  setShowDeleteMemberModal?: (a: boolean) => void;
  onChangeWorkspaceMembersRole?: (a: string, b: WorkspaceRole) => void;
  onResendInvitationEmail?: (
    member: ResendWorkspaceInvitationMemberDto
  ) => void;
  onSort: (value: string) => void;
  isLoading: boolean;
}

export const MemberStatus = (status: string) => {
  const selectTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag style={activeStyle}>{status}</Tag>;
      case 'pending':
        return <Tag style={pendingStyle}>{status}</Tag>;

      case 'expired':
        return <Tag style={expiredStyle}>{status}</Tag>;
      default:
        return null;
    }
  };
  return selectTag(status);
};

function WorkspaceMembersTable({
  onChangeWorkspaceMembersRole,
  setShowDeleteMemberModal,
  onResendInvitationEmail,
  setMembersToManage,
  onSort,
  count,
  edges,
  isLoading,
  selectedIds
}: IWorkspaceMembersTable) {
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace();
  const dispatch = useDispatch();
  const maxSeatsReached =
    currentOrganization.seatsCount.count >=
    (currentOrganization.entity?.limitsV2.users ?? Number.POSITIVE_INFINITY);
  const [visibleGroupsPopover, setVisibleGroupsPopover] = React.useState<
    string | null
  >(null);
  const columns = [
    {
      // eslint-disable-next-line react/no-unstable-nested-components
      title: () => {
        const copy = (
          <>
            User <span className="users_count">{count}</span>
          </>
        );
        return copy;
      },
      dataIndex: 'name',
      key: 'name',
      render: (text: string, node: WorkspaceMemberDto) => {
        const { invitationEmail, user, workspaceRole, invitationExpired } =
          node;
        return (
          <div className="user_record">
            <div className="user_record__avatar">
              <UserAvatar
                isActive={!invitationEmail}
                size="large"
                src={user?.picture.url || ''}
                userEmail={user?.email || ''}
                userName={user?.name || ''}
              />
            </div>
            <div className="user_record__info">
              <div className="name">{user?.name}</div>
              <div className="email main-body-text main-body-text--tiny main-body-text--dark-gray">
                {user?.email || invitationEmail}
                {!!invitationEmail && onResendInvitationEmail && (
                  <Button
                    className="email-resend"
                    type="link"
                    size="small"
                    disabled={maxSeatsReached}
                    onClick={() => {
                      if (invitationEmail) {
                        onResendInvitationEmail({
                          invitationEmail
                        });
                      }
                    }}
                  >
                    Resend
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text: string, node: WorkspaceMemberDto) => {
        const { organizationRole, workspaceRole, id, permissions } = node;
        const role =
          organizationRole === 'organization://workspace-member'
            ? workspaceRole
            : organizationRole;
        if (!permissions.changeRole || !onChangeWorkspaceMembersRole) {
          const isAdmin = node.organizationRole === 'organization://admin';
          return workspaceRole && !isAdmin
            ? WorkspaceRoleCopy[workspaceRole]
            : OrganizationRoleCopy[organizationRole];
        }
        return (
          <div className="user_role">
            <Select
              value={role}
              style={{ width: 128 }}
              dropdownMatchSelectWidth={180}
              onChange={(e) => {
                onChangeWorkspaceMembersRole(id, e as WorkspaceRole);
              }}
            >
              <Select.Option value="workspace://team-lead">
                Workspace lead
              </Select.Option>
              <Select.Option value="workspace://member">Member</Select.Option>
              <Select.Option value="workspace://contributor">
                Contributor
              </Select.Option>
              <Select.Option disabled value="organization://admin">
                Admin
              </Select.Option>
            </Select>
          </div>
        );
      },
      width: 190,
      sorter: true
    },
    {
      title: 'Projects',
      key: 'campaigns',
      dataIndex: 'campaignsCount',
      width: 90,
      render: (_: any, node: WorkspaceMemberDto) => {
        return (
          <MemberProjectsPopover
            memberId={node.id}
            campaignsCount={node.campaignsCount}
            workspaceId={currentWorkspace.id}
          />
        );
      }
    },
    {
      title: 'Groups',
      key: 'groups',
      dataIndex: 'groups',
      width: 90,
      render: (_: any, node: WorkspaceMemberDto) => {
        const { groups } = node;
        return (
          <Popover
            content={
              <div>
                <div className="user_campaigns__title">
                  Groups ({groups.length})
                </div>
                {groups.map((group) => {
                  return (
                    <div key={group.id} className="user_campaigns__list">
                      <Button
                        block
                        className="user_campaigns__name"
                        type="link"
                        onClick={() => {
                          setVisibleGroupsPopover(null);
                          dispatch(
                            toggleWorkspaceMemberGroupModal({
                              group,
                              workspace: currentWorkspace,
                              visible: true
                            })
                          );
                        }}
                      >
                        <Avatar
                          size="small"
                          shape="square"
                          style={{
                            background: hashedColor(
                              group.id,
                              'memberGroupAvatar'
                            ),
                            marginRight: 16
                          }}
                        >
                          {group.name[0].toUpperCase()}
                        </Avatar>
                        {group.name}
                      </Button>
                    </div>
                  );
                })}
              </div>
            }
            className={classNames({
              user_campaigns: true,
              'user_campaigns--empty': !groups.length
            })}
            placement="bottomLeft"
            overlayClassName="user_campaigns__popover"
            trigger={groups.length ? ['hover'] : []}
            open={visibleGroupsPopover === node.id}
            onOpenChange={(open) => {
              if (open) {
                setVisibleGroupsPopover(node.id);
              } else {
                setVisibleGroupsPopover(null);
              }
            }}
          >
            <span>{groups.length}</span>
          </Popover>
        );
      }
    },
    {
      title: 'Status',
      key: 'isActive',
      dataIndex: 'status',
      render: (text: string, node: WorkspaceMemberDto) => {
        const status = node.user
          ? 'active'
          : node.invitationExpired
          ? 'expired'
          : 'pending';
        return MemberStatus(status);
      },
      width: 90,
      sorter: true
    },
    {
      title: '',
      key: 'action',
      dataIndex: 'action',
      width: 89,
      render: (text: string, node: WorkspaceMemberDto) => {
        const { permissions } = node;

        if (
          !permissions.delete ||
          !setShowDeleteMemberModal ||
          !setMembersToManage
        ) {
          return;
        }

        return (
          <Button
            type="link"
            onClick={() => {
              setMembersToManage([node.id], [node], false);
              setShowDeleteMemberModal(true);
            }}
            style={{ cursor: 'pointer', float: 'right' }}
            className="main-body-text main-body-text--semibold main-body-text--dark-gray"
          >
            Remove
          </Button>
        );
      }
    }
  ];
  return (
    <Table
      pagination={false}
      columns={columns}
      dataSource={edges || []}
      rowKey={(node) => node.id}
      loading={isLoading || !edges}
      rowSelection={
        setMembersToManage
          ? {
              type: 'checkbox',
              selectedRowKeys: selectedIds,
              preserveSelectedRowKeys: true,
              onChange: (selectedRowKeys, selectedRows, info) => {
                setMembersToManage(
                  selectedRowKeys as string[],
                  selectedRows,
                  info.type === 'all'
                );
              }
            }
          : undefined
      }
      onChange={(pagination, filters, sorterResult) => {
        const sorter = Array.isArray(sorterResult)
          ? sorterResult[0]
          : sorterResult;
        if (sorter) {
          onSort(
            `${sorter.columnKey}:${sorter.order === 'ascend' ? 'ASC' : 'DESC'}`
          );
        } else {
          onSort('createdAt:DESC');
        }
      }}
    />
  );
}

export default WorkspaceMembersTable;
